import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Search, X } from "lucide-react";
import { fetchMedications } from "../lib/api";
import { useIsMobile } from "../hooks/useIsMobile";

const CHIPS = [
  { key: "tretinoin", label: "Using tretinoin?", display: "Tretinoin" },
  { key: "isotretinoin", label: "On Accutane?", display: "Accutane (isotretinoin)" },
  { key: "doxycycline", label: "Taking doxycycline?", display: "Doxycycline" },
  { key: "retinol", label: "Using retinol?", display: "Retinol" },
  { key: "spironolactone", label: "On spironolactone?", display: "Spironolactone" },
];

const normalizeMeds = (data) => {
  const list = Array.isArray(data) ? data : data?.medications || data?.items || [];
  return list.map((m) => ({
    id: m.id,
    label: m.generic_name || (m.brand_names && m.brand_names[0]) || m.id,
    brands: m.brand_names || [],
  }));
};

function MaskButton({ children, onClick, variant = "primary", href, testid }) {
  const base =
    "btn-mask group h-[3.4rem] px-7 rounded-full text-[15px] font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]";
  const styles =
    variant === "primary"
      ? "bg-ink text-cream-50 shadow-card hover:bg-ink-soft"
      : "bg-white text-ink border border-surface-line hover:shadow-card";
  const inner = (
    <span className="btn-mask-inner">
      <span className="btn-label">{children}</span>
      <span className="btn-label-hover" aria-hidden>{children}</span>
    </span>
  );
  if (href) {
    return (
      <a href={href} data-testid={testid} className={`${base} ${styles}`}>{inner}</a>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid={testid} className={`${base} ${styles}`}>
      {inner}
    </button>
  );
}

export default function HomeHero() {
  const navigate = useNavigate();
  const [meds, setMeds] = useState([]);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [product, setProduct] = useState("");

  const checkerRef = useRef(null);
  const medInputRef = useRef(null);
  const productRef = useRef(null);
  const isMobile = useIsMobile();
  const heroVideoSrc = isMobile
    ? "/media/blendsafe-face-scan-hero-mobile.mp4"
    : "/media/blendsafe-face-scan-hero.mp4";
  const heroVideoPoster = isMobile
    ? "/media/blendsafe-face-scan-hero-mobile-poster.jpg"
    : "/media/blendsafe-face-scan-hero-poster.jpg";

  useEffect(() => {
    let cancelled = false;
    fetchMedications()
      .then((d) => { if (!cancelled) setMeds(normalizeMeds(d)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const matches = useMemo(() => {
    const t = query.trim().toLowerCase();
    if (!t) return meds.slice(0, 6);
    return meds
      .filter((m) => m.label.toLowerCase().includes(t) || m.brands.some((b) => b.toLowerCase().includes(t)))
      .slice(0, 6);
  }, [query, meds]);

  const focusChecker = () => {
    checkerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => medInputRef.current?.focus(), 350);
  };
  const goToChecker = (medId, productName = "") => {
    const params = new URLSearchParams({ med: medId });
    if (productName.trim()) params.set("product", productName.trim());
    navigate(`/routine?${params.toString()}`);
  };
  const pickChip = (chip) => goToChecker(chip.key, product);
  const runCheck = () => {
    if (!selected) { focusChecker(); setOpen(true); return; }
    const params = new URLSearchParams({ med: selected.id });
    if (product.trim()) params.set("product", product.trim());
    navigate(`/check?${params.toString()}`);
  };

  return (
    <section
      data-testid="home-hero"
      className="relative bg-cream-50 overflow-hidden border-b border-surface-line"
    >
      {/* Background dots video — rendered exactly like the template */}
      <div aria-hidden className="tpl-hero-layer">
        <div className="tpl-bg-layer">
          <video className="tpl-bg-video" autoPlay loop muted playsInline poster="/template/dots_poster.jpg">
            <source src="/template/dots.webm" type="video/webm" />
            <source src="/template/dots.mp4" type="video/mp4" />
          </video>
          <div className="tpl-hero-tint" />
        </div>
        <div className="tpl-layer-gradient" />
      </div>

      <div className="relative z-[1] max-w-screen-xl mx-auto px-4 sm:px-6 md:px-10 pt-14 sm:pt-20 md:pt-24 pb-10 md:pb-14">
        {/* Centered header (template hero) */}
        <div className="max-w-3xl mx-auto text-center">
          <span data-animate="load-hero-1" className="eyebrow-cap mx-auto">
            <span className="w-1.5 h-1.5 rounded-full bg-[#1f3d2e]" />
            Medication-aware skincare
          </span>
          <h1
            data-testid="hero-headline"
            data-animate="load-hero-title"
            className="mt-6 text-[34px] leading-[1.06] sm:text-5xl lg:text-[62px] lg:leading-[1.02] font-extrabold tracking-[-0.035em] text-ink"
          >
            Using skincare while taking medication?{" "}
            <span className="font-editorial">Get clear answers in seconds.</span>
          </h1>
          <p
            data-testid="hero-subheadline"
            data-animate="load-hero-2"
            className="mt-5 text-[15px] sm:text-lg text-ink-soft/90 leading-relaxed max-w-xl mx-auto"
          >
            Find out what&rsquo;s safe, what needs caution, and what to avoid—in seconds.
          </p>
          <div data-animate="load-hero-3" className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <MaskButton variant="primary" onClick={runCheck} testid="hero-primary-cta">
              Check My Product <ArrowRight className="w-4 h-4" />
            </MaskButton>
            <MaskButton variant="secondary" href="/#how" testid="hero-secondary-cta">
              See How It Works
            </MaskButton>
          </div>

          {/* Product hero video — Full Face Scan explainer */}
          <div data-animate="load-hero-4" className="mt-10">
            <div
              data-testid="hero-video-card"
              className="relative rounded-[22px] overflow-hidden border border-surface-line bg-white shadow-floating"
            >
              <video
                key={heroVideoSrc}
                data-testid="hero-video"
                className="block w-full h-auto"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                poster={heroVideoPoster}
              >
                <source src={heroVideoSrc} type="video/mp4" />
              </video>
            </div>
          </div>
        </div>

        {/* Showcase — the functional checker as the hero's centerpiece */}
        <div data-animate="load-hero-4" className="mt-12 md:mt-14">
          <div className="mb-3 max-w-3xl mx-auto">
            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-mute mb-2.5 text-center">
              Common searches today
            </p>
            <div data-testid="common-search-chips" className="flex flex-wrap justify-center gap-2">
              {CHIPS.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  data-testid={`search-chip-${c.key}`}
                  onClick={() => pickChip(c)}
                  className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border border-surface-line bg-white/90 text-[12.5px] font-medium text-ink-soft hover:border-ink/30 hover:text-ink hover:-translate-y-0.5 transition-all"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#a87a32]" />
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div
            ref={checkerRef}
            data-testid="home-checker"
            className="relative max-w-3xl mx-auto rounded-[22px] border border-surface-line bg-white shadow-floating p-5 sm:p-7"
          >
            <span
              aria-hidden
              className="pointer-events-none absolute -inset-px rounded-[22px]"
              style={{ boxShadow: "inset 0 0 0 1px rgba(168,122,50,0.14)" }}
            />
            <div className="relative grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-semibold text-ink-mute mb-1.5">Your medication</label>
                <div className="relative">
                  {selected ? (
                    <div className="flex items-center justify-between gap-2 h-12 px-4 rounded-xl border border-[#1f3d2e]/30 bg-[#f0f4f1]">
                      <span data-testid="home-checker-selected-med" className="text-[15px] font-semibold text-ink truncate">{selected.label}</span>
                      <button
                        type="button"
                        data-testid="home-checker-clear-med"
                        onClick={() => { setSelected(null); setTimeout(() => medInputRef.current?.focus(), 0); }}
                        className="text-ink-mute hover:text-ink"
                        aria-label="Clear medication"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center h-12 px-3.5 rounded-xl border border-surface-line bg-cream-50 focus-within:border-ink-mute focus-within:ring-4 focus-within:ring-cream-100 transition-all">
                        <Search className="w-4 h-4 text-ink-light shrink-0" />
                        <input
                          ref={medInputRef}
                          data-testid="home-checker-med-input"
                          value={query}
                          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                          onFocus={() => setOpen(true)}
                          placeholder="Tretinoin, Accutane, Doxycycline…"
                          className="flex-1 bg-transparent px-2.5 text-[15px] text-ink placeholder:text-ink-light focus:outline-none"
                        />
                      </div>
                      {open && matches.length > 0 && (
                        <ul
                          data-testid="home-checker-med-options"
                          className="absolute z-20 mt-1.5 w-full rounded-xl border border-surface-line bg-white shadow-floating overflow-hidden max-h-64 overflow-y-auto"
                        >
                          {matches.map((m) => (
                            <li key={m.id}>
                              <button
                                type="button"
                                data-testid={`home-checker-med-option-${m.id}`}
                                onClick={() => goToChecker(m.id, product)}
                                className="w-full text-left px-4 py-2.5 text-[14px] text-ink-soft hover:bg-cream-50 hover:text-ink transition-colors"
                              >
                                {m.label}
                                {m.brands?.length > 0 && (
                                  <span className="text-ink-light text-[12px]"> · {m.brands.slice(0, 2).join(", ")}</span>
                                )}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-[12px] font-semibold text-ink-mute mb-1.5">Skincare product <span className="font-normal text-ink-light">(optional)</span></label>
                <input
                  ref={productRef}
                  data-testid="home-checker-product-input"
                  value={product}
                  onChange={(e) => setProduct(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") runCheck(); }}
                  placeholder="The Ordinary Niacinamide, CeraVe…"
                  className="w-full h-12 px-4 rounded-xl border border-surface-line bg-cream-50 text-[15px] text-ink placeholder:text-ink-light focus:outline-none focus:border-ink-mute focus:ring-4 focus:ring-cream-100 transition-all"
                />
              </div>
            </div>

            <button
              type="button"
              data-testid="home-checker-submit"
              onClick={runCheck}
              className="relative mt-4 w-full rounded-xl text-cream-50 text-[15px] font-semibold inline-flex items-center justify-center gap-2 transition-all hover:scale-[1.005] active:scale-[0.99]"
              style={{ height: "3.25rem", background: "linear-gradient(135deg,#1f3d2e 0%,#2c5240 50%,#1f3d2e 100%)", boxShadow: "0 12px 24px -10px rgba(31,61,46,0.45)" }}
            >
              Check My Product
              <ArrowRight className="w-4 h-4" />
            </button>
            <p className="relative mt-3 text-[11.5px] text-ink-mute text-center">Free safety checks · No credit card · Built for US consumers</p>
          </div>
        </div>
      </div>
    </section>
  );
}
